import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { HlmSpinnerComponent, ToastService } from '../../../ui';
import { BrandService } from '../brand.service';
import { Brand, BrandCreateRequest, BrandUpdateRequest } from '../brand.model';
import { ConfirmDialogComponent } from '../../../shared/confirm-dialog/confirm-dialog.component';
import { mapHttpError } from '../brand-error.util';
import { BrandFormComponent } from './brand-form.component';

/**
 * Route wrapper for `/admin/brands/new` and `/admin/brands/:brandId/edit`.
 *
 * Kicks off all 9 lookup fetches in parallel on init. In edit mode also loads
 * the target brand. Passes everything to {@link BrandFormComponent} and handles
 * the service calls emitted back.
 */
@Component({
  selector: 'cmt-brand-form-page',
  standalone: true,
  imports: [BrandFormComponent, HlmSpinnerComponent],
  template: `
    @if (loadingBrand()) {
      <div class="flex items-center justify-center gap-3 p-12 text-sm text-muted-foreground">
        <hlm-spinner size="20" /> Loading brand…
      </div>
    } @else {
      <cmt-brand-form
        [brand]="brand()"
        [languages]="service.languages()"
        [priceLists]="service.priceLists()"
        [dialingCodes]="service.dialingCodes()"
        [dialingCodesLoading]="service.dialingCodesLoading()"
        [skills]="service.skills()"
        [skillsets]="service.skillsets()"
        [vatRegimes]="service.vatRegimes()"
        [bookkeepingConfigs]="service.bookkeepingConfigs()"
        [saving]="saving()"
        [togglingActivation]="togglingActivation()"
        [savedTick]="savedTick()"
        (submitCreate)="onCreate($event)"
        (submitUpdate)="onUpdate($event)"
        (toggleActive)="onToggleActive($event)"
      />
    }
  `,
})
export class BrandFormPageComponent implements OnInit {
  readonly service = inject(BrandService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly toastService = inject(ToastService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  /** Edit mode: blocks the form behind a spinner until the brand GET resolves
   *  (the form needs the brand row to hydrate). Create mode: stays false —
   *  the form renders immediately and individual selects show their own
   *  per-lookup spinners while data trickles in. */
  readonly loadingBrand = signal(false);
  readonly saving = signal(false);
  readonly togglingActivation = signal(false);
  readonly brand = signal<Brand | null>(null);
  /** Bumped after each successful edit-mode save to flash "Saved ✓" on the button. */
  readonly savedTick = signal(0);

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('brandId');
    const brandId = idParam ? Number(idParam) : null;

    // Each lookup tolerates a 404 so a flaky endpoint doesn't nuke the whole
    // page. Brand GET stays load-bearing below.
    const empty = catchError(() => of([]));
    const isEdit = brandId !== null && !Number.isNaN(brandId);
    const lookups = forkJoin({
      languages: this.service.loadLanguages().pipe(empty),
      priceLists: this.service.loadPriceLists(isEdit ? brandId! : undefined).pipe(empty),
      dialingCodes: this.service.loadDialingCodes().pipe(empty),
      skills: this.service.loadSkills().pipe(empty),
      skillsets: this.service.loadSkillsets().pipe(empty),
      vatRegimes: this.service.loadVatRegimes().pipe(empty),
      bookkeepingConfigs: this.service.loadBookkeepingConfigs().pipe(empty),
    });

    // Lookups always fire in the background; per-select spinners surface their
    // loading state. The page-level guard only blocks edit mode while the
    // brand row is in flight.
    lookups.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      error: () => this.toastService.open('Failed to load lookups', 'OK', { duration: 5000 }),
    });

    if (brandId !== null && !Number.isNaN(brandId)) {
      this.loadingBrand.set(true);
      this.service.getBrand(brandId)
        .pipe(finalize(() => this.loadingBrand.set(false)), takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: brand => this.brand.set(brand),
          error: () => this.toastService.open('Failed to load brand', 'OK', { duration: 5000 }),
        });
    }
  }

  onCreate(body: BrandCreateRequest): void {
    this.saving.set(true);
    this.service.createBrand(body).pipe(finalize(() => this.saving.set(false)), takeUntilDestroyed(this.destroyRef)).subscribe({
      next: created => {
        this.toastService.open(`Brand created: ${created.name}`, 'OK', { duration: 4000 });
        this.router.navigate(['/admin/brands']);
      },
      error: err => this.showError(err, 'Failed to create brand'),
    });
  }

  onUpdate(diff: BrandUpdateRequest): void {
    const current = this.brand();
    if (!current) return;
    this.saving.set(true);
    this.service.updateBrand(current.id, diff).pipe(finalize(() => this.saving.set(false)), takeUntilDestroyed(this.destroyRef)).subscribe({
      next: updated => {
        this.brand.set(updated);
        this.savedTick.update(t => t + 1);
      },
      error: err => this.showError(err, 'Failed to save brand'),
    });
  }

  onToggleActive(next: boolean): void {
    const current = this.brand();
    if (!current) return;
    if (!next) {
      // Deactivation is the destructive direction — gate behind a confirm so
      // an accidental click can't pull a live brand offline. Reactivation has
      // no negative blast radius and stays one click.
      this.dialog
        .open(ConfirmDialogComponent, {
          data: {
            title: 'Deactivate brand?',
            message: `${current.name} will stop accepting new customers and disappear from most lists. Existing customers and billing remain unchanged. You can reactivate any time.`,
            confirmLabel: 'Deactivate',
            cancelLabel: 'Cancel',
            confirmColor: 'warn',
            icon: 'warning',
          },
          width: '440px',
          maxWidth: '92vw',
          autoFocus: false,
          panelClass: 'cmt-dialog-panel',
        })
        .afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(confirmed => {
          if (confirmed) this.runToggle(current.id, false);
        });
      return;
    }
    this.runToggle(current.id, true);
  }

  private runToggle(id: number, next: boolean): void {
    this.togglingActivation.set(true);
    const call = next ? this.service.activateBrand(id) : this.service.deactivateBrand(id);
    call.pipe(finalize(() => this.togglingActivation.set(false)), takeUntilDestroyed(this.destroyRef)).subscribe({
      next: updated => {
        this.brand.set(updated);
        this.toastService.open(next ? 'Brand activated' : 'Brand deactivated', 'OK', { duration: 3000 });
      },
      error: err => this.showError(err, next ? 'Failed to activate' : 'Failed to deactivate'),
    });
  }

  private showError(err: unknown, fallback: string): void {
    // Whitelist the message by status (LoggingAndErrorHandlingGuidelines §9.2) —
    // never surface the raw backend `error.message` to the operator.
    const message = mapHttpError(err, {
      400: 'Invalid input — check the form and try again.',
      404: 'Brand not found.',
      409: 'A brand with these details already exists.',
    }, fallback);
    this.toastService.open(message, 'OK', { duration: 5000 });
  }
}
